import React, { Component } from "react";
import styled from "styled-components";

import * as ApiErrors from "apis/errors";
import { ActionPermissionsObject } from "../../../../common/interfaces/index";
import { SlideCategories } from "../../../../common/SlideCategories/CategoryController";
import { Button } from "../../../Components/Button";
import { Dropdown } from "../../../Components/Dropdown";
import { Icon } from "../../../Components/Icon";
import { MenuItem } from "../../../Components/Menu";
import { ToggleSwitch } from "../../../Components/ToggleSwitch";
import { WithLabel } from "../../../Components/WithLabel";
import WorkspaceController from "../../../controllers/WorkspaceController";
import getLogger, { LogGroup } from "../../../core/logger";
import { Slide } from "../../../core/models/slide";
import { FileType } from "../../../core/utilities/fileSourcesAndTypes";
import { PropertyPanelContainer, PropertySection } from "../../../EditorComponents/PropertyPanel";
import { ShowDialog, ShowErrorDialog } from "../../../react/components/Dialogs/BaseDialog";
import { FlexSpacer } from "../../../react/components/Gap";
import { FlexBox } from "../../../react/components/LayoutGrid";
import { SimpleLabel } from "../../../react/components/SimpleLabel";
import { themeColors } from "../../../react/sharedStyles";
import ImportFileDialog from "../../../react/views/ImportFile/ImportFileDialog";
import { _ } from "../../../vendor";
import PresentationEditorController from "../../PresentationEditor/PresentationEditorController";
import { AiIcon } from "../AiIcon";
import { AIImageSourcePicker, AiImageStylePopup } from "../GenerateImage/AiImageStyle";
import { AiSlideContentEditor } from "../GeneratePresentation/Components/AiSlideContentEditor";
import FileEntry from "../GeneratePresentation/Components/FileEntry";
import { LanguagePicker } from "../GeneratePresentation/Components/LanguagePicker";
import SlideAiController from "./SlideAiController";
import SlideAiIdeasList from "./SlideAiIdeasList";

const logger = getLogger(LogGroup.AI);

const Pane = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    pointer-events: auto;
`;

const Container = styled.div`
    width: 100%;
    height: 100%;
    display: grid;
    grid-template-areas: 
"prompt options"
"preview preview";
    grid-template-rows: 400px 1fr;
    grid-template-columns: 1fr 320px;
    background: #f9f9f9;
    row-gap: 10px;
`;

const Header = styled.div`
    height: 50px;
    background: #333;
    color: white;
    font-weight: bold;
    font-size: 18px;
    padding: 10px 10px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 10px;
`;

const PromptContainer = styled.div`
    grid-area: prompt;
    padding: 15px 20px 0px;
    display: grid;
    grid-template-rows: auto 1fr auto;
`;

const OptionsContainer = styled.div`
    grid-area: options;
    padding: 15px 20px 0px 0px;
    display: grid;
    grid-template-rows: auto 1fr;
`;

const PreviewContainer = styled.div`
    grid-area: preview;
    overflow: hidden;
    height: 100%;
`;

const TextContainer = styled.div`
    background: #fff;
    border: solid 1px #ccc;
    padding: 10px;
    width: 100%;
    pointer-events: auto;
    overflow-y: auto;
`;

class GenerateNewSlidePane extends Component {
    constructor(props) {
        super(props);

        const presentationMetadata = props.presentation.get("metadata") || {};

        this.state = {
            currentSlide: null,
            isGenerating: false,
            isGenerated: false,
            slidePrompt: "",
            slideType: "auto",
            preserveExactText: false,
            usePresentationAsContext: true,
            language: presentationMetadata.language ?? "auto",
            webSearch: false,
            isDragging: false,
            isSavingFiles: false,
            imageSource: presentationMetadata.imageSource || "ai",
            imageStyle: presentationMetadata.imageStyle,
            imageModel: null,
        };

        const canGenerate = WorkspaceController.actionPermissions[ActionPermissionsObject.GENERATIVE_AI].use && WorkspaceController.actionPermissions[ActionPermissionsObject.GENERATIVE_AI_IMAGES].use;
        if (this.state.imageSource === "ai" && !canGenerate) {
            this.state.imageSource = "web";
        }

        this.promptRef = React.createRef();
        this.containerRef = React.createRef();
    }

    componentDidMount() {
        let { presentation } = this.props;

        let dummySlide = new Slide({
            id: "--new--",
            presentationId: presentation.id,
        }, {
            disconnected: true,
            autoLoad: false,
            presentation,
        });
        SlideAiController.setCurrentSlide(dummySlide);

        this.setState({ currentSlide: dummySlide });
    }

    handleGenerateSlide = async () => {
        let { slideType, language, preserveExactText, usePresentationAsContext, imageSource, imageStyle, imageModel, webSearch } = this.state;

        let slidePrompt = this.promptRef.current.getMarkdown();

        this.setState({ isGenerating: true, isGenerated: false });

        try {
            await SlideAiController.regenerateIdeas(slidePrompt, {
                slideType,
                preserveExactText,
                usePresentationAsContext,
                language,
                webSearch,
                imageStyle,
                imageModel,
                imageSource,
                regenerateSlideImage: true,
            });
        } catch (err) {
            logger.error(err, "[GenerateNewSlidePane] handleGenerateSlide() error", { slidePrompt, slideType, preserveExactText, usePresentationAsContext, language, webSearch, imageStyle, imageModel, imageSource });

            ShowErrorDialog({
                title: "Error generating slide ideas",
                message: "Sorry, we couldn't generate slide ideas. Please try again.",
            });
            this.setState({ isGenerating: false, isGenerated: false });
            return;
        }

        if (this.props.slideCategories.length === 0) {
            this.setState({ isGenerating: false, isGenerated: false });
        } else {
            this.setState({ isGenerating: false, isGenerated: true });
        }
    }

    handleSelectIdea = idea => {
        const { onGeneratedSlide } = this.props;
        onGeneratedSlide(idea);
    }

    handleStartOver = () => {
        this.setState({ isGenerating: false, isGenerated: false });
    }

    handleDragOver = e => {
        e.preventDefault();
        e.stopPropagation();
        if (!this.state.isDragging) {
            this.setState({ isDragging: true });
        }
    }

    handleDragLeave = e => {
        e.preventDefault();
        e.stopPropagation();

        if (e.currentTarget === this.containerRef.current) {
            this.setState({ isDragging: false });
        }
    }

    handleDrop = async e => {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ isDragging: false });

        let droppedFiles = Array.from(e.dataTransfer.files);

        for (let file of droppedFiles) {
            await this.processFile(file);
        }
    }

    handleAddFile = () => {
        ShowDialog(ImportFileDialog, {
            fileTypes: [FileType.Document, FileType.Presentation, FileType.Spreadsheet],
            closeOnSuccess: true,
            onSuccess: async file => {
                if (file) {
                    this.processFile(file);
                }
            }
        });
    }

    processFile = async file => {
        try {
            await SlideAiController.parseDocument(file);
        } catch (err) {
            logger.error(err, "[GenerateNewSlidePane] processFile() error", { fileName: file.name, fileSize: file.size });

            if (err instanceof ApiErrors.ContentTooLargeError) {
                ShowErrorDialog({
                    title: "File is too large",
                    message: err.message,
                });
                return;
            }

            ShowErrorDialog({
                title: `Error processing ${file.name}`,
                message: "Sorry, we couldn't process the file. Please try again with a different file.",
            });
        }
    }

    handleRemoveFile = file => {
        SlideAiController.removeFile(file.name);
    }

    render() {
        let { currentTheme, files } = this.props;
        let { slidePrompt, imageSource, imageStyle, preserveExactText, usePresentationAsContext, slideType, language, isGenerating, isGenerated, webSearch, isDragging } = this.state;

        if (isGenerating || isGenerated) {
            return (
                <Pane>
                    {isGenerated &&
                        <Header>
                            <Button unframed textColor="white" hoverColor="#555" onClick={this.handleStartOver}>
                                <Icon>replay</Icon>
                                Start Over
                            </Button>
                        </Header>
                    }
                    <PreviewContainer>
                        <SlideAiIdeasList onSelect={this.handleSelectIdea} />
                    </PreviewContainer>
                </Pane>
            );
        }

        return (
            <Pane>
                <Container onDragOver={this.handleDragOver} onDragLeave={this.handleDragLeave} onDrop={this.handleDrop} ref={this.containerRef}>
                    <PromptContainer>
                        <FlexBox left middle>
                            <SimpleLabel>SLIDE PROMPT</SimpleLabel>
                            <FlexSpacer />
                        </FlexBox>
                        <TextContainer>
                            <AiSlideContentEditor ref={this.promptRef} text={slidePrompt} onChange={slidePrompt => this.setState({ slidePrompt })} />
                        </TextContainer>
                        <FlexBox left middle gap={20}>
                            <WithLabel right tight label="Enable Web Search">
                                <ToggleSwitch value={webSearch} onChange={webSearch => this.setState({ webSearch })} />
                            </WithLabel>
                            <Button unframed onClick={this.handleAddFile}>
                                <Icon>{isDragging ? "check_circle" : "add_circle"}</Icon>
                                {isDragging ? "Drop files here" : "Add file..."}
                            </Button>
                        </FlexBox>
                        {files.length > 0 && (
                            <FlexBox wrap gap={10} horizontalAlign="start" fillWidth>
                                {files.map((file, index) => (
                                    <FileEntry key={index} file={file} onRemove={this.handleRemoveFile} />
                                ))}
                            </FlexBox>
                        )}
                    </PromptContainer>
                    <OptionsContainer>
                        <FlexBox left middle>
                            <SimpleLabel>OPTIONS</SimpleLabel>
                        </FlexBox>
                        <PropertyPanelContainer fullHeight style={{ border: "solid 1px #ccc" }}>
                            <PropertySection>
                                <WithLabel label="Slide Type">
                                    <Dropdown value={slideType} onChange={slideType => this.setState({ slideType })}>
                                        <MenuItem value="auto">Auto Detect</MenuItem>
                                        {Object.keys(SlideCategories).map(key => (
                                            <MenuItem key={key} value={SlideCategories[key]}>{_.startCase(_.toLower(SlideCategories[key]))}</MenuItem>
                                        ))}
                                    </Dropdown>
                                </WithLabel>
                                <WithLabel label="Language">
                                    <LanguagePicker value={language} onChange={language => this.setState({ language })} />
                                </WithLabel>
                            </PropertySection>
                            <PropertySection>
                                <WithLabel left label="Use Presentation Context" toolTip="Generate this slide using the context of the entire presentation.">
                                    <ToggleSwitch value={usePresentationAsContext} onChange={usePresentationAsContext => this.setState({ usePresentationAsContext })} />
                                </WithLabel>
                            </PropertySection>
                            <PropertySection>
                                <WithLabel left label="Preserve Exact Text" toolTip="Try to keep the text in the generated slides exactly as it appears in the prompt.">
                                    <ToggleSwitch value={preserveExactText} onChange={preserveExactText => this.setState({ preserveExactText })} />
                                </WithLabel>
                            </PropertySection>
                            <PropertySection>
                                <WithLabel label="Image Source">
                                    <AIImageSourcePicker value={imageSource} onChange={imageSource => this.setState({ imageSource })} />
                                </WithLabel>
                                {imageSource == "ai" && (
                                    <WithLabel label="Ai Image Style">
                                        <AiImageStylePopup theme={currentTheme} imageStyle={imageStyle} onChange={(imageStyle, imageModel) => this.setState({ imageStyle, imageModel })} />
                                    </WithLabel>
                                )}
                            </PropertySection>
                            <FlexSpacer />
                            <PropertySection fullWidth color={themeColors.lightBlue}>
                                <FlexBox fill>
                                    <Button
                                        large
                                        color={themeColors.aiColor}
                                        disabled={isGenerating}
                                        onClick={this.handleGenerateSlide}
                                        height={48}
                                        style={{ padding: "0 10px", margin: "10px 10px", width: "100%" }}
                                        showSpinner={isGenerating}
                                    >
                                        <AiIcon />
                                        Generate Slide
                                    </Button>
                                </FlexBox>
                            </PropertySection>
                        </PropertyPanelContainer>
                    </OptionsContainer>

                </Container>
            </Pane>
        );
    }
}

export default PresentationEditorController.withState(SlideAiController.withState(GenerateNewSlidePane));
